import React, { useEffect, useState } from "react";
import Draggable from "react-draggable";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
} from "@mui/material";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

function PaperComponent(props) {
  return (
    <Draggable handle="#draggable-ft-ficheiros-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

const thStyle = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#fff",
  textAlign: "left",
  padding: "8px 10px",
  fontSize: 12,
  color: "#7b809a",
  boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
};

const tdStyle = {
  padding: "8px 10px",
  borderTop: "1px solid #eee",
  fontSize: 12,
  color: "#1a2b4c",
};

export default function DialogFichaTransporteFicheiros({
  open,
  onClose,
  idFichaTransporte,
  titulo,
}) {
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const idutilizador = userData?.id || 0;

  const isAdminLike =
    temFuncao("Administração") || temFuncao("Informática") || temFuncao("Tesouraria");

  const isArmazem = temFuncao("Armazém");

  const podeLer = isAdminLike || isArmazem;
  const podeCriar = isAdminLike || isArmazem;
  const podeApagar = isAdminLike || isArmazem;

  const [loading, setLoading] = useState(false);
  const [ficheiros, setFicheiros] = useState([]);
  const [canCamera, setCanCamera] = useState(false);

  useEffect(() => {
    try {
      const ua = String(navigator?.userAgent || "");
      const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
      setCanCamera(isMobile);
    } catch {
      setCanCamera(false);
    }
  }, []);

  const inputGhostStyle = {
    position: "absolute",
    left: "-9999px",
    width: 1,
    height: 1,
    opacity: 0,
    pointerEvents: "none",
  };

  const fechar = () => {
    setFicheiros([]);
    onClose?.();
  };

  const isImagem = (nome = "") => {
    const n = String(nome).toLowerCase();
    return (
      n.endsWith(".jpg") ||
      n.endsWith(".jpeg") ||
      n.endsWith(".png") ||
      n.endsWith(".webp") ||
      n.endsWith(".gif")
    );
  };

  const formatBytes = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n) || n <= 0) return "—";

    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;

    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatDataHora = (value) => {
    if (!value) return "—";

    try {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) {
        const pad = (x) => String(x).padStart(2, "0");
        return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(
          d.getHours()
        )}:${pad(d.getMinutes())}`;
      }
    } catch {}

    return String(value);
  };

  const getExtensao = (nome = "") => {
    const n = String(nome);
    const p = n.lastIndexOf(".");
    if (p < 0) return "";
    return n.slice(p + 1).toUpperCase();
  };

  const abrirFicheiro = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const carregar = async () => {
    if (!open) return;
    if (!idFichaTransporte) return;

    if (!podeLer) {
      showAlert("Sem permissões para consultar ficheiros.", "error");
      return;
    }

    setLoading(true);
    try {
      const [data, err, meta] = await handleApi(
        api.post("Distribuicao/SelFichasTransporteFicheiros", {
          idfichatransporte: idFichaTransporte,
          idutilizador,
        })
      );

      if (err) {
        showAlert(err, "error");
        setFicheiros([]);
        return;
      }

      setFicheiros(Array.isArray(data) ? data : []);
      if (meta?.message) showAlert(meta.message, meta.severity || "success");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      await carregar();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idFichaTransporte]);

  const compressImageIfNeeded = async (file) => {
    try {
      if (!file) return file;

      const type = String(file.type || "").toLowerCase();
      const name = String(file.name || "").toLowerCase();

      const isImg =
        type.startsWith("image/") ||
        name.endsWith(".jpg") ||
        name.endsWith(".jpeg") ||
        name.endsWith(".png") ||
        name.endsWith(".webp");

      if (!isImg) return file;
      if (file.size <= 350 * 1024) return file;

      const MAX_DIM = 1600;
      const QUALITY = 0.75;

      let bitmap;
      if ("createImageBitmap" in window) {
        bitmap = await createImageBitmap(file);
      } else {
        bitmap = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
      }

      const w = bitmap.width;
      const h = bitmap.height;

      let nw = w;
      let nh = h;

      if (w >= h && w > MAX_DIM) {
        nw = MAX_DIM;
        nh = Math.round((h * MAX_DIM) / w);
      } else if (h > w && h > MAX_DIM) {
        nh = MAX_DIM;
        nw = Math.round((w * MAX_DIM) / h);
      }

      const canvas = document.createElement("canvas");
      canvas.width = nw;
      canvas.height = nh;

      const ctx = canvas.getContext("2d", { alpha: false });
      ctx.drawImage(bitmap, 0, 0, nw, nh);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
      if (!blob) return file;

      const base = file.name?.replace(/\.[^/.]+$/, "") || "imagem";
      const newName = `${base}.jpg`;

      const compressed = new File([blob], newName, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      if (compressed.size >= file.size) return file;

      return compressed;
    } catch {
      return file;
    }
  };

  const onUploadFiles = async (files) => {
    if (!podeCriar) {
      showAlert("Sem permissões para carregar ficheiros.", "error");
      return;
    }
    if (!idFichaTransporte) {
      showAlert("Ficha de Transporte inválida.", "error");
      return;
    }
    if (!Array.isArray(files) || files.length === 0) return;

    setLoading(true);

    let ok = 0;
    let falhas = 0;
    let primeiraFalha = null;

    try {
      for (const file of files) {
        try {
          const fileFinal = await compressImageIfNeeded(file);

          const fd = new FormData();
          fd.append("idfichatransporte", String(idFichaTransporte));
          fd.append("idutilizador", String(idutilizador));
          fd.append("ficheiro", fileFinal);

          const [_, err] = await handleApi(
            api.post("Distribuicao/UploadFichasTransporteFicheiro", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            })
          );

          if (err) {
            falhas++;
            if (!primeiraFalha) primeiraFalha = err;
            continue;
          }

          ok++;
        } catch {
          falhas++;
          if (!primeiraFalha) primeiraFalha = "Erro ao carregar ficheiro.";
        }
      }

      if (ok > 0) showAlert(`Upload concluído: ${ok} ficheiro(s).`, "success");
      if (falhas > 0)
        showAlert(`Falharam ${falhas} ficheiro(s). ${primeiraFalha || ""}`, "warning");

      await carregar();
    } finally {
      setLoading(false);
    }
  };

  const pedirApagar = async (nome) => {
    if (!podeApagar) return showAlert("Sem permissões para eliminar.", "error");
    if (!nome) return;

    setLoading(true);
    try {
      const [_, err, meta] = await handleApi(
        api.post("Distribuicao/DelFichasTransporteFicheiro", {
          idfichatransporte: idFichaTransporte,
          nome,
          idutilizador,
        })
      );

      if (err) return showAlert(err, "error");
      if (meta?.message) showAlert(meta.message, meta.severity || "success");

      await carregar();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={fechar} maxWidth="md" fullWidth PaperComponent={PaperComponent}>
      <DialogTitle id="draggable-ft-ficheiros-title" sx={{ cursor: "move" }}>
        {titulo ||
          `Anexos da Ficha de Transporte${idFichaTransporte ? ` (ID: ${idFichaTransporte})` : ""}`}
      </DialogTitle>

      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <DialogContent>
        <MDBox display="flex" gap={2} alignItems="flex-end" flexWrap="wrap" mt={0.5}>
          <MDBox display="flex" gap={1} alignItems="center" mb={0.25}>
            <input
              id="ft-ficheiros-upload"
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                onUploadFiles(files);
                e.target.value = "";
              }}
              disabled={!podeCriar}
            />

            <MDButton
              color="success"
              disabled={!podeCriar || loading}
              startIcon={<CloudUploadIcon />}
              onClick={() => document.getElementById("ft-ficheiros-upload")?.click?.()}
            >
              Upload
            </MDButton>

            {canCamera && (
              <MDButton
                color="info"
                disabled={!podeCriar || loading}
                startIcon={<PhotoCameraIcon />}
                component="label"
              >
                Câmera
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={inputGhostStyle}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) onUploadFiles([f]);
                  }}
                />
              </MDButton>
            )}
          </MDBox>
        </MDBox>

        <MDBox mt={2}>
          <MDTypography variant="button" color="text" fontWeight="regular" sx={{ fontSize: 12 }}>
            Ficheiros ({ficheiros.length})
          </MDTypography>

          <MDBox mt={1} sx={{ borderRadius: 1, overflow: "auto", border: "1px solid #eee" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 100 }}>Ações</th>
                  <th style={thStyle}>Nome</th>
                  <th style={{ ...thStyle, width: 84 }}>Miniat.</th>
                  <th style={thStyle}>Tamanho</th>
                  <th style={thStyle}>Atualizado</th>
                </tr>
              </thead>

              <tbody>
                {ficheiros.map((f) => {
                  const nome = f?.nome || "";
                  const url = f?.url || "";
                  const imagem = isImagem(nome);

                  return (
                    <tr key={nome}>
                      <td style={{ ...tdStyle, width: 100, whiteSpace: "nowrap" }}>
                        <Tooltip title="Abrir">
                          <span>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => abrirFicheiro(url)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!podeApagar || !nome}
                              onClick={() => pedirApagar(nome)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </td>

                      <td
                        style={{
                          ...tdStyle,
                          maxWidth: 420,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={nome || ""}
                      >
                        {nome || "—"}
                      </td>

                      <td style={{ ...tdStyle, width: 84 }}>
                        {imagem && url ? (
                          <img
                            src={url}
                            alt={nome}
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #ddd",
                              cursor: "pointer",
                              display: "block",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                            }}
                            onClick={() => abrirFicheiro(url)}
                          />
                        ) : (
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 8,
                              border: "1px solid #ddd",
                              background: "#f8f9fa",
                              color: "#7b809a",
                              fontSize: 11,
                              fontWeight: 700,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              textTransform: "uppercase",
                            }}
                            title={getExtensao(nome) || "ficheiro"}
                          >
                            {getExtensao(nome) || "—"}
                          </div>
                        )}
                      </td>

                      <td style={tdStyle}>{f?.tamanhoFormatado || formatBytes(f?.tamanho)}</td>
                      <td style={tdStyle}>
                        {formatDataHora(f?.dataAtualizacao || f?.modificadoem)}
                      </td>
                    </tr>
                  );
                })}

                {ficheiros.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center", color: "#7b809a" }}>
                      Sem ficheiros.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </MDBox>
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton onClick={fechar} color="secondary">
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
